import type { SorterResult } from "antd/lib/table/interface";
import { ResourceName } from "core/router/config/ResourceName";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ChangeButton } from "src/app/features/ManualDasConfiguration/components/buttons/ChangeButton";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayAcquirerButtons,
    displayEnumValue,
    displayStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { canAccess } from "../../../../../core/router/auth/permissions";
import type { ManualDasConfigurationEntity } from "../../types/ManualDasConfigurationEntity";

export const ManualDasConfigurationsTable: FC<ResourceTableProps<ManualDasConfigurationEntity>> = ({
    onStateChanged,
    onRowSelectionChange,
    datasource,
    resource,
}) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<ManualDasConfigurationEntity>): ExtendedColumnsType<ManualDasConfigurationEntity>[] => {
            return [
                {
                    title: t("manualDasConfigs.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("manualDasConfigs.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("manualDasConfigs.siteId"),
                    key: "siteId",
                    dataIndex: "siteId",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "siteId" ? sortInfo.order : null,
                },
                {
                    title: t("manualDasConfigs.parentUnit"),
                    key: "parentUnit",
                    dataIndex: "parentUnit",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "parentUnit" ? sortInfo.order : null,
                    render: (value) => displayEnumValue(value, "site-group-provider"),
                },
                {
                    title: t("manualDasConfigs.activeDasAcquirers"),
                    key: "activeDasAcquirers",
                    dataIndex: "activeDasAcquirers",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "activeDasAcquirers" ? sortInfo.order : null,
                    render: (value) => displayAcquirerButtons(value, "secondary"),
                },
                {
                    title: t("manualDasConfigs.disabledDasAcquirers"),
                    key: "disabledAcquirers",
                    dataIndex: "disabledAcquirers",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "disabledAcquirers" ? sortInfo.order : null,
                    render: (value) => displayAcquirerButtons(value, "danger"),
                },
                {
                    title: t("manualDasConfigs.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("manualDasConfigs.selectToSwitch"),
                    key: "selectToSwitch",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "selectToSwitch" ? sortInfo.order : null,
                    render: (_, record) =>
                        canAccess(ResourceName.MANUAL_DAS_CONFIGURATION, "canWrite") ? <ChangeButton item={record} /> : null,
                },
            ];
        },
        [t],
    );

    return (
        (datasource && (
            <GenericTable<ManualDasConfigurationEntity>
                name="manualDasConfigurationTables_table"
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                resource={resource}
            />
        )) ||
        null
    );
};
